import React, { useMemo } from 'react';
import TwDialog from '../TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwSelect, { TwSelectOption } from '../TwSelects/TwSelects';
import { useFormik } from 'formik';
import { Trip } from '../../redux/api/trips/trips.model';
import './TwEditEquipmentModal.scss';
import {
  useGetTripByIdQuery,
  useGetTripByTokenQuery,
  usePatchEquipmentByTokenMutation,
  usePatchTripMutation
} from '../../redux/api/trips/tripApi';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { Theme, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import RTKUtils from '../../utils/rtkUtils';

interface TwEditEquipmentModalProps {
  open: boolean;
  idType: string;
  onClose: () => void;
  segmentId: string;
  equipmentList: TwSelectOption[];
  token?: string;
}

export const TwEditEquipmentModal: React.FC<TwEditEquipmentModalProps> = ({
  idType,
  equipmentList,
  open,
  onClose,
  segmentId,
  token = ''
}): JSX.Element => {
  const { addNotification } = useNotifications();
  const route = useLocation();

  const { data: { trip: tripFromToken = {} as Trip } = {} } = useGetTripByTokenQuery(token, {
    skip: !token.length
  });
  const { data: tripFromOrder = {} as Trip } = useGetTripByIdQuery(segmentId, {
    skip: token.length > 0
  });
  const trip = token.length > 0 ? tripFromToken : tripFromOrder;

  const [patchTrip] = usePatchTripMutation();
  const [patchEquipmentByToken] = usePatchEquipmentByTokenMutation();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const equipmentKey = `${idType}_id` as keyof Trip;
  const initialValue = trip[equipmentKey] ?? '';
  const readableType = idType[0].toUpperCase() + idType.slice(1);

  const tagsToInvalidate = RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: { equipmentId: initialValue },
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: (values) => {
      onClose();
      (token
        ? patchEquipmentByToken({
            tripId: trip.id,
            token,
            equipmentKey,
            equipmentId: (values.equipmentId as string) || null
          })
        : patchTrip({
            id: trip.id || '',
            body: { [equipmentKey]: values.equipmentId || null },
            tagsToInvalidate
          })
      )
        .unwrap()
        .then(({ data }) => {
          const newEquipment = data[idType]?.name;
          const message = newEquipment
            ? `${newEquipment} has been successfully assigned.`
            : `Equipment has been successfully unassigned.`;

          addNotification({
            message,
            variant: 'success'
          });
        })
        .catch(() => {
          addNotification({
            message: `Error updating ${readableType} name. Please try again.`,
            variant: 'error'
          });
          setFieldValue('equipmentId', initialValue);
        });
    }
  });

  function handleClose(): void {
    onClose();
    setFieldValue('equipmentId', initialValue);
  }

  return (
    <TwDialog
      title={`Assign ${idType}`}
      open={open}
      onClickClose={handleClose}
      size="sm"
      classNameActions="edit-equipment-modal__actions"
      fullScreen={matchesMobileOrTablet}
      actions={[
        <TwButton
          key="1"
          fullwidth={false}
          disabled={false}
          type={TwButtonType.secondary}
          label="Cancel"
          action={handleClose}
        />,
        <TwButton
          key="2"
          fullwidth={false}
          disabled={false}
          type={TwButtonType.primary}
          label="Assign"
          action={handleSubmit}
        />
      ]}
    >
      <TwSelect
        optionList={equipmentList}
        label={readableType}
        name={'equipmentId'}
        value={values.equipmentId as string}
        onInputChange={handleChange}
        className="edit-equipment-modal__select"
      />
    </TwDialog>
  );
};
